import { useCallback, useEffect, useRef, useState } from 'react';
import { loadTurnstile } from '../../lib/turnstile';

export type TurnstileStatus = 'loading' | 'ready' | 'blocked';

// Invisible/managed Cloudflare Turnstile challenge (roadmap item #2's bot
// defense on signup) - Cloudflare decides whether the visitor needs an
// interactive puzzle at all; most real visitors never see one. Renders
// nothing when VITE_TURNSTILE_SITE_KEY is unset, so a dev environment (or
// a deploy that hasn't configured a real Cloudflare site yet) behaves
// exactly as before this existed - the backend's VerifyTurnstile makes the
// matching no-op decision on an empty/missing token.
//
// The backend's VerifyTurnstile fails CLOSED on an empty token whenever
// TURNSTILE_SECRET_KEY is configured (production) - it does NOT fail open
// just because this session never got one. So when the script can't load
// at all (ad/privacy blocker, corporate firewall blocking
// challenges.cloudflare.com, a flaky embedded webview), a real human was
// previously stuck silently submitting an empty token and getting "bot
// check failed - please try again" forever, since nothing ever re-tried
// loading the widget. `status` surfaces that failure to the caller so it
// can show an explanation instead of a dead-end generic error, and
// `onRetry` lets the user re-attempt the load without a full page reload.
export function TurnstileWidget({
  onToken,
  onStatusChange,
}: {
  onToken: (token: string) => void;
  onStatusChange?: (status: TurnstileStatus) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const [status, setStatus] = useState<TurnstileStatus>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let cancelled = false;
    setStatus('loading');

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            setStatus('ready');
            onToken(token);
          },
          'expired-callback': () => onToken(''),
          'error-callback': () => {
            // A render-time error (not a script-load failure) - the
            // widget itself can't proceed, so treat it the same as a
            // blocked load: no token is coming without a retry.
            if (!cancelled) setStatus('blocked');
            onToken('');
          },
        });
      })
      .catch(() => {
        // Cloudflare unreachable/blocked (ad blocker, firewall, offline,
        // ...) - surface it instead of leaving the caller to wonder why
        // submitting keeps failing.
        if (!cancelled) setStatus('blocked');
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, attempt]);

  const retry = useCallback(() => {
    onToken('');
    setAttempt((n) => n + 1);
  }, [onToken]);

  if (!siteKey) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div ref={containerRef} />
      {status === 'blocked' && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Couldn&apos;t load the verification check - this can happen with an ad/privacy blocker or a restrictive
          network.{' '}
          <button type="button" onClick={retry} className="underline hover:no-underline">
            Retry
          </button>
          , or try disabling any content blocker for this site.
        </p>
      )}
    </div>
  );
}
